import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Dialog } from "@/components/dialog";
import { Modal } from "@/components/modal";
import { Step, Stepper, useStepper } from "@/components/stepper";

import { ResumeSessionPrompt } from "./components/ResumeSessionPrompt";
import {
  STEP_MESSAGE,
  STEP_RESULTS,
  STEP_SOURCE_UPLOAD,
  STEP_UPLOAD,
} from "./constants";
import {
  DesignConfidenceCheckerProvider,
  useDcc,
} from "./contexts/DesignConfidenceCheckerProvider";
import { MessageStep } from "./steps/MessageStep";
import { ResultsStep } from "./steps/ResultsStep";
import { SourceUploadStep } from "./steps/SourceUploadStep";
import { UploadStep } from "./steps/UploadStep";

interface DesignConfidenceCheckerModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after `POST /uploading-sessions/:id/submit` returns 200. */
  onSubmitted?: (sessionId: string) => void;
  /** Parent request being delivered. Required to scope the session. */
  requestId: number;
}

export function DesignConfidenceCheckerModal({
  isOpen,
  onOpenChange,
  onSubmitted,
  requestId,
}: DesignConfidenceCheckerModalProps) {
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (!isOpen) return null;

  return (
    <DesignConfidenceCheckerProvider
      isOpen={isOpen}
      onClose={handleClose}
      onSubmitted={onSubmitted}
      requestId={requestId}
    >
      <DesignConfidenceCheckerModalShell
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      />
    </DesignConfidenceCheckerProvider>
  );
}

function DesignConfidenceCheckerModalShell({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { session, shell } = useDcc();
  const [currentStep, setCurrentStep] = useState(STEP_UPLOAD);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(STEP_UPLOAD);
    }
  }, [isOpen]);

  const handleCloseRequest = shell.handleCloseRequest;

  if (shell.isResolvingSession) return null;

  return (
    <Modal
      classNames={{
        modal: twMerge(
          "flex flex-col overflow-hidden transition-[width,height] duration-300",
          shell.showResumePrompt
            ? "w-[480px] max-w-[calc(100vw-2rem)]"
            : currentStep === STEP_RESULTS
              ? "w-[80vw] h-[80vh]"
              : "w-[70vw] h-[70vh]",
        ),
      }}
      isDismissable={false}
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (open) {
          onOpenChange(open);
        } else {
          handleCloseRequest();
        }
      }}
    >
      <Dialog
        className={twMerge(
          "flex flex-col overflow-hidden",
          !shell.showResumePrompt && "flex-1",
        )}
      >
        {shell.showResumePrompt ? (
          <ResumeSessionPrompt
            isBusy={session.activeSessionQuery.isFetching}
            onClose={handleCloseRequest}
            onContinue={() => {
              void session.resumeSession();
            }}
            onStartFresh={() => {
              void session.startFresh();
            }}
          />
        ) : (
          <Stepper
            className="relative flex flex-1 flex-col overflow-hidden"
            initialStep={STEP_UPLOAD}
            totalSteps={4}
          >
            <StepperContent
              currentStep={currentStep}
              onCurrentStepChange={setCurrentStep}
            />
          </Stepper>
        )}
      </Dialog>
    </Modal>
  );
}

interface StepperContentProps {
  currentStep: number;
  onCurrentStepChange: (step: number) => void;
}

function StepperContent({
  currentStep,
  onCurrentStepChange,
}: StepperContentProps) {
  const { shell } = useDcc();
  const { goToStep, nextStep } = useStepper();

  const goToResults = () => {
    nextStep();
    onCurrentStepChange(STEP_RESULTS);
  };

  const goBackToResults = () => {
    goToStep(STEP_RESULTS);
    onCurrentStepChange(STEP_RESULTS);
  };

  const goToMessage = () => {
    nextStep();
    onCurrentStepChange(STEP_MESSAGE);
  };

  const goToSourceUpload = () => {
    nextStep();
    onCurrentStepChange(STEP_SOURCE_UPLOAD);
  };

  const goToUpload = () => {
    goToStep(STEP_UPLOAD);
    onCurrentStepChange(STEP_UPLOAD);
  };

  const goToSourceUploadSkip = () => {
    goToStep(STEP_SOURCE_UPLOAD);
    onCurrentStepChange(STEP_SOURCE_UPLOAD);
  };

  const goBackFromSourceUpload = () => {
    if (shell.attachedFileCount === 0) {
      goToUpload();

      return;
    }

    goToStep(STEP_MESSAGE);
    onCurrentStepChange(STEP_MESSAGE);
  };

  return (
    <>
      {currentStep === STEP_RESULTS && (
        <ResultsHeader onClose={shell.handleCloseOnly} />
      )}

      <Step className="flex h-full flex-col" id={STEP_UPLOAD}>
        <UploadStep
          onCheckFiles={goToResults}
          onClose={shell.handleCloseOnly}
          onSkipToSourceFiles={goToSourceUploadSkip}
        />
      </Step>

      <Step className="flex flex-1 flex-col overflow-hidden" id={STEP_RESULTS}>
        <ResultsStep onBack={goToUpload} onContinue={goToMessage} />
      </Step>

      <Step className="flex h-full flex-col" id={STEP_MESSAGE}>
        <MessageStep
          onBack={goBackToResults}
          onClose={shell.handleCloseOnly}
          onSubmit={goToSourceUpload}
        />
      </Step>

      <Step className="flex h-full flex-col" id={STEP_SOURCE_UPLOAD}>
        <SourceUploadStep
          onBack={goBackFromSourceUpload}
          onClose={shell.handleCloseOnly}
          onSubmit={() => {
            void shell.handleSubmit();
          }}
        />
      </Step>
    </>
  );
}

interface ResultsHeaderProps {
  onClose: () => void;
}

function ResultsHeader({ onClose }: ResultsHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b border-border-secondary px-6 py-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-base font-semibold text-text-primary">
          Design confidence checker
        </span>
        <span className="text-sm text-text-tertiary">
          Evaluate your files before delivering to the customer.
        </span>
      </div>
      <button
        aria-label="Close"
        className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-bg-secondary"
        onClick={onClose}
        type="button"
      >
        <X size={20} />
      </button>
    </div>
  );
}
